import { AxiosError } from 'axios'
import { ERROR_CODES, ErrorKinds } from './ErrorCodes'
import { GenericException } from './GenericException'

export const getFormattedErrorWithChildren = (
  errorCodesbject: { [key in any]: any },
  errorCode: string,
  childErrorCode?: string
) => {
  if (!errorCode) {
    return undefined
  }

  if (childErrorCode) {
    return Object.values(errorCodesbject).find(
      ({ code, subtype }) => errorCode === code && childErrorCode === subtype
    )
  }

  return Object.values(errorCodesbject).find(({ code }) => errorCode === code)
}

// Copy pasted from generated GLP API since all APIs have same error format. Intentionally not references any specific GLP API because it fits any of them

/**
 *
 * @export
 * @interface GLPChildErrorResponse
 */
interface GLPChildErrorResponse extends GenericException {
  /**
   * A link to supporting documentation for this API
   * @type {string}
   * @memberof GLPChildErrorResponse
   */
  developerLink?: string
  /**
   * Identifies the element in the request which has caused the error to occur. This will not come in case of any of the authorization header elements are invalid or missing
   * @type {string}
   * @memberof GLPChildErrorResponse
   */
  path?: string
}
/**
 *
 * @export
 * @interface GLPErrorResponse
 */
export interface GLPErrorResponse extends GenericException {
  /**
   * A link to supporting documentation for this API
   * @type {string}
   * @memberof GLPErrorResponse
   */
  developerLink?: string
  /**
   * Present for certain errors (e.g. validation) where more than error may have occurred.
   * @type {Array<GLPChildErrorResponse>}
   * @memberof GLPErrorResponse
   */
  childError?: Array<GLPChildErrorResponse>
}

const defaultBusinessMessage = 'Something went wrong, please check the details and try again'

const defaultSystem = 'GLP'

const requestUnauthorized = 'REQUEST_UNAUTHORIZED'

// GLP specific errors
const GLP_ERROR_CODES: { [key in any]: any } = {
  DATA_INVALID_400: {
    httpStatus: 400,
    code: 'REQUEST_INVALID',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: 'DATA_INVALID',
    description: 'Request data is invalid',
    developerMessage: 'Request data is invalid',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  CUSTOMER_NOT_FOUND: {
    httpStatus: 400,
    code: 'MEMBERSHIP_NOT_FOUND',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: 'CUSTOMER_NOT_FOUND',
    description: 'Customer Not Found',
    developerMessage: 'Customer Not Found',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  TOKEN_INVALID: {
    httpStatus: 401,
    code: requestUnauthorized,
    kind: ErrorKinds.UNAUTHORIZED,
    subtype: 'TOKEN_INVALID',
    description: 'Token validation failed',
    developerMessage: 'Token validation failed',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: false,
  },
  TOKEN_EXPIRED: {
    httpStatus: 401,
    code: requestUnauthorized,
    kind: ErrorKinds.UNAUTHORIZED,
    subtype: 'TOKEN_EXPIRED',
    description: 'Token expired',
    developerMessage: 'Token expired',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  DATA_INVALID: {
    httpStatus: 401,
    code: requestUnauthorized,
    kind: ErrorKinds.UNAUTHORIZED,
    subtype: 'DATA_INVALID',
    description: 'Token data/scope validation failed',
    developerMessage: 'Token data/scope validation failed',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  AUTHORIZATION_LEVEL_INSUFFICIENT: {
    httpStatus: 401,
    code: requestUnauthorized,
    kind: ErrorKinds.UNAUTHORIZED,
    subtype: 'AUTHORIZATION_LEVEL_INSUFFICIENT',
    description: 'Token authentication level validation failed',
    developerMessage: 'Token authentication level validation failed',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  DEVELOPER_INACTIVE: {
    httpStatus: 403,
    code: 'DEVELOPER_INACTIVE',
    kind: ErrorKinds.FORBIDDEN,
    subtype: 'DEVELOPER_INACTIVE',
    description: 'The provided API key is no longer active and access to this API is forbidden',
    developerMessage:
      'The provided API key is no longer active and access to this API is forbidden',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  DEVELOPER_NOT_AUTHORIZED: {
    httpStatus: 403,
    code: 'DEVELOPER_NOT_AUTHORIZED',
    kind: ErrorKinds.FORBIDDEN,
    subtype: 'DEVELOPER_NOT_AUTHORIZED',
    description: 'The provided API key does not provide access to this API',
    developerMessage: 'The provided API key does not provide access to this API',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  USAGE_RATE_EXCEEDED: {
    httpStatus: 403,
    code: 'USAGE_RATE_EXCEEDED',
    kind: ErrorKinds.FORBIDDEN,
    subtype: 'USAGE_RATE_EXCEEDED',
    description: 'The usage rate (calls per second) has been exceeded for the API Key.',
    developerMessage: 'The usage rate (calls per second) has been exceeded for the API Key.',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  USAGE_RATE_LIMIT_EXCEEDED: {
    httpStatus: 403,
    code: 'USAGE_RATE_LIMIT_EXCEEDED',
    kind: ErrorKinds.FORBIDDEN,
    subtype: 'USAGE_RATE_LIMIT_EXCEEDED',
    description: 'The usage rate limit (calls per day) has been exceeded for the API Key.',
    developerMessage: 'The usage rate limit (calls per day) has been exceeded for the API Key.',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  SSL_REQUIRED: {
    httpStatus: 403,
    code: 'SSL_REQUIRED',
    kind: ErrorKinds.FORBIDDEN,
    subtype: 'SSL_REQUIRED',
    description: 'This API must be accessed using SSL.',
    developerMessage: 'This API must be accessed using SSL.',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  RESOURCE_NOT_FOUND: {
    httpStatus: 404,
    code: 'RESOURCE_NOT_FOUND',
    kind: ErrorKinds.NOT_FOUND,
    subtype: 'RESOURCE_NOT_FOUND',
    description: 'Resource not found',
    developerMessage: 'Resource not found',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  METHOD_NOT_SUPPORTED: {
    httpStatus: 405,
    code: 'REQUEST_INVALID',
    kind: ErrorKinds.METHOD_NOT_ALLOWED,
    subtype: 'METHOD_NOT_SUPPORTED',
    description: 'Http method is unsupported',
    developerMessage: 'Http method is unsupported',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  INTERNAL_SERVER_ERROR: {
    httpStatus: 500,
    code: 'INTERNAL_SERVER_ERROR',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: 'INTERNAL_SERVER_ERROR',
    description: 'Any other technical failure',
    developerMessage: 'Any other technical failure',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
  SYSTEM_UNAVAILABLE: {
    httpStatus: 503,
    code: 'SYSTEM_UNAVAILABLE',
    kind: ErrorKinds.SERVICE_UNAVAILABLE,
    subtype: 'SYSTEM_UNAVAILABLE',
    description: 'When a system is not available',
    developerMessage: 'When a system is not available',
    businessMessage: defaultBusinessMessage,
    system: defaultSystem,
    unrecoverable: true,
  },
}

const defaultErr = ERROR_CODES.PROVIDER_FAILED_UNRECOVERABLE

export class GLPAPIException extends GenericException {
  constructor(error: AxiosError<GLPErrorResponse>) {
    const errorResponse = error.response?.data
    const specificErrorResponse = errorResponse?.childError?.[0]

    const errorByCode = errorResponse
      ? getFormattedErrorWithChildren(
          GLP_ERROR_CODES,
          errorResponse.code,
          specificErrorResponse?.code
        )
      : error

    const glpGeneratedError = {
      httpStatus: errorByCode?.httpStatus || defaultErr.httpStatus,
      code: errorByCode?.code || defaultErr.code,
      kind: specificErrorResponse ? specificErrorResponse?.kind : errorResponse?.kind,
      subtype: specificErrorResponse ? specificErrorResponse?.code : errorResponse?.code,
      businessMessage: errorByCode?.businessMessage || defaultErr.businessMessage,
      developerMessage: errorByCode?.developerMessage || defaultErr.developerMessage,
      unrecoverable: !error.response?.status || error.response.status === 500,
      originalError: error,
      description: errorByCode?.description || defaultErr.description,
    }

    super(glpGeneratedError)
  }
}
